import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, getDocFromServer, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

interface ComingSoonContextType {
  isComingSoonEnabled: boolean;
  toggleComingSoon: () => void;
  loading: boolean;
}

const ComingSoonContext = createContext<ComingSoonContextType | undefined>(undefined);

export const useComingSoon = () => {
  const context = useContext(ComingSoonContext);
  if (context === undefined) {
    throw new Error('useComingSoon must be used within a ComingSoonProvider');
  }
  return context;
};

interface ComingSoonProviderProps {
  children: React.ReactNode;
}

export const ComingSoonProvider: React.FC<ComingSoonProviderProps> = ({ children }) => {
  const [isComingSoonEnabled, setIsComingSoonEnabled] = useState(true); // Default to true
  const [loading, setLoading] = useState(false); // Start as false since we're defaulting to enabled

  useEffect(() => {
    const settingsRef = doc(db, 'settings', 'comingSoon');
    const run = async () => {
      try {
        const snap = await getDocFromServer(settingsRef).catch(() => getDoc(settingsRef));
        if (snap.exists()) {
          const data = snap.data();
          setIsComingSoonEnabled(data?.enabled ?? true);
        } else {
          await setDoc(settingsRef, { enabled: true }, { merge: true });
          setIsComingSoonEnabled(true);
        }
      } catch {
        setIsComingSoonEnabled(true);
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, []);

  const toggleComingSoon = async () => {
    try {
      const settingsRef = doc(db, 'settings', 'comingSoon');
      const newValue = !isComingSoonEnabled;
      
      await setDoc(settingsRef, { 
        enabled: newValue,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin' // You can enhance this with actual user info
      });
      
      // Don't manually set state here - let the onSnapshot listener handle it
      // setIsComingSoonEnabled(newValue);
    } catch (error) {
      console.error('Error toggling coming soon setting:', error);
      throw error; // Re-throw the error so the component can handle it
    }
  };

  const value = {
    isComingSoonEnabled,
    toggleComingSoon,
    loading
  };

  return (
    <ComingSoonContext.Provider value={value}>
      {children}
    </ComingSoonContext.Provider>
  );
};
